import { useEffect, useMemo, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { buildGateRuntimeHtml } from "modules/gate/services/gateRuntimeHtml";
import { invokeGateHostMethodWithContext, isGateMethodAllowed } from "modules/gate/services/gateBridgeHost";
import {
    GateApiScope,
    GateBridgeRequestMessage,
    GateBridgeResponseMessage,
    GateDefinition,
    GateLocationSnapshot,
    GateWidgetDefinition,
} from "modules/gate/types/gate";
import { ModalResult } from "./modalService";
import { usePlatformTheme } from "app/providers/PlatformThemeProvider";
import { GATE_VUE_RUNTIME_QUERY_KEY, fetchGateVueRuntimeSource } from "modules/gate/services/gateVendorAssets";

interface GateWidgetModalFrameProps {
    widget: GateWidgetDefinition;
    data?: Record<string, unknown>;
    onResolve: (result: ModalResult) => void;
    onReject: (error: Error) => void;
    notify: (message: string, level?: string) => void;
}

const postGateMessage = (target: Window | null | undefined, message: GateBridgeResponseMessage) => {
    if (!target) return;
    target.postMessage(JSON.parse(JSON.stringify(message)), "*");
};

const hostNavigate = (to: string | number) => {
    if (typeof to === "number") {
        window.location.reload();
        return;
    }

    if (to) {
        window.location.assign(to);
    }
};

const hostLocation = (): GateLocationSnapshot => ({
    origin: window.location.origin,
    href: window.location.href,
    pathname: window.location.pathname,
    routePath: "/",
    search: window.location.search,
    hash: window.location.hash,
    params: {},
    query: Object.fromEntries(new URLSearchParams(window.location.search).entries()),
});

export const GateWidgetModalFrame = ({
    widget,
    data = {},
    onResolve,
    onReject,
    notify,
}: GateWidgetModalFrameProps) => {
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const { activeTheme } = usePlatformTheme();

    const page = useMemo<GateDefinition>(() => ({
        name: `modal-${widget.code}`,
        slug: `modal-${widget.code}`,
        title: widget.name || widget.code,
        template: `<component :is="page.meta.widgetCode" v-bind="page.meta.modalData"></component>`,
        script: "({ template: page.template })",
        meta: {
            widgetCode: widget.code,
            modalData: data,
        },
        widgets: [{
            ...widget,
            props: data,
        }],
        apiScope: widget.apiScope || [],
    }), [data, widget]);

    const { data: vueRuntimeSource } = useQuery({
        queryKey: GATE_VUE_RUNTIME_QUERY_KEY,
        queryFn: fetchGateVueRuntimeSource,
        staleTime: Infinity,
        retry: false,
    });

    const runtimeHtml = useMemo(() => vueRuntimeSource ? buildGateRuntimeHtml({
        page,
        allowedMethods: page.apiScope || [],
        i18n: {
            locale: "en-US",
            fallbackLocale: "en-US",
            messages: {},
        },
        location: hostLocation(),
        vueRuntimeSource,
        theme: activeTheme,
        modal: { data },
    }) : "", [activeTheme, data, page, vueRuntimeSource]);

    useEffect(() => {
        const handleMessage = async (event: MessageEvent<GateBridgeRequestMessage>) => {
            if (!iframeRef.current || event.source !== iframeRef.current.contentWindow) {
                return;
            }

            if (!event.data || event.data.source !== "gate-runtime" || event.data.type !== "gate:invoke") {
                return;
            }

            const responseBase: Pick<GateBridgeResponseMessage, "source"> = {
                source: "gate-host",
            };

            if (event.data.method === "modal.submit" || event.data.method === "modal.close") {
                postGateMessage(iframeRef.current.contentWindow, {
                    ...responseBase,
                    type: "gate:resolve",
                    requestId: event.data.requestId,
                    payload: { ok: true },
                });
                onResolve(normalizeModalResult(event.data.payload));
                return;
            }

            if (!isGateMethodAllowed(page.apiScope || [], event.data.method)) {
                postGateMessage(iframeRef.current.contentWindow, {
                    ...responseBase,
                    type: "gate:reject",
                    requestId: event.data.requestId,
                    error: `Gate API scope denied for method "${event.data.method}"`,
                });
                return;
            }

            if (event.data.widgetCode) {
                const widgetScope = widget.apiScope || [];

                if (!widgetScope.includes(event.data.method as GateApiScope)) {
                    postGateMessage(iframeRef.current.contentWindow, {
                        ...responseBase,
                        type: "gate:reject",
                        requestId: event.data.requestId,
                        error: `Gate widget API scope denied for method "${event.data.method}" on widget "${event.data.widgetCode}"`,
                    });
                    return;
                }
            }

            try {
                const payload = await invokeGateHostMethodWithContext(event.data.method as GateApiScope, event.data.payload, {
                    navigate: hostNavigate as any,
                    notify,
                });

                postGateMessage(iframeRef.current.contentWindow, {
                    ...responseBase,
                    type: "gate:resolve",
                    requestId: event.data.requestId,
                    payload,
                });
            } catch (error) {
                const normalizedError = error instanceof Error ? error : new Error("Unknown Gate error");
                postGateMessage(iframeRef.current.contentWindow, {
                    ...responseBase,
                    type: "gate:reject",
                    requestId: event.data.requestId,
                    error: normalizedError.message,
                });
                onReject(normalizedError);
            }
        };

        window.addEventListener("message", handleMessage);

        return () => {
            window.removeEventListener("message", handleMessage);
        };
    }, [notify, onReject, onResolve, page.apiScope, widget.apiScope]);

    return (
        <iframe
            ref={iframeRef}
            sandbox="allow-scripts allow-forms allow-popups allow-modals"
            srcDoc={runtimeHtml}
            title={page.title || page.name}
            style={{ width: "100%", height: "100%", border: "none" }}
        />
    );
};

const normalizeModalResult = (payload: unknown): ModalResult => {
    if (!payload || typeof payload !== "object") {
        return { action: "close" };
    }

    const result = payload as Record<string, unknown>;

    return {
        action: String(result.action || "close"),
        data: result.data,
    };
};
